import React, { useEffect, useState } from 'react';
import * as debounce from 'lodash.debounce';
import { translate } from '../Services/translation';
import { Provider, useDispatch, useSelector } from 'react-redux';
import {
  removeOrderRowGroup,
  toggle,
  update,
  triggerDataLayerAddToCart,
} from '../Actions/Cart.action';
import { ShoppingCart, Package, Trash, X as CloseIcon } from 'phosphor-react';
import QuantityInput from './QuantityInput';
import CopyButton from './CopyButton';
import SideMenu from './SideMenu';
import colors from 'Exports/colors.scss';
import renderForServer from '../render-for-server';
import ProductInStockTag from './ProductInStockTag';

const MiniCart = () => {
  const dispatch = useDispatch();
  const {
    quantity,
    orderRows,
    fees,
    grandTotal,
    vat,
    checkoutUrl,
    showInfo,
    addToCartAnimating = false,
    discount,
    showMiniCartGrandTotal,
    miniCartGrandTotal,
    gtmGrandTotal,
  } = useSelector((state) => state.cart);
  const onToggle = () => dispatch(toggle());
  const onClose = () => dispatch(toggle(false));

  const [abortController, setAbortController] = useState();

  // Must be set after client initialized
  useEffect(() => setAbortController(new AbortController()), []);

  useEffect(() => {
    if (showInfo === true) {
      dispatch(triggerDataLayerAddToCart(orderRows, gtmGrandTotal));
    }
  }, [showInfo]);

  const updateOrderRowQuantity = debounce(
    (articleNumber, quantity, rowSystemId) =>
      dispatch(update(articleNumber, quantity, rowSystemId, abortController)),
    200
  );

  const hasOrderRows = orderRows && Object.keys(orderRows).length > 0;

  const footer = (
    <>
      {fees &&
        fees.map((fee) => (
          <div key={fee.name + fee.cost} className="cart__sidemenu-footer-row">
            <span>{fee.name}</span>
            <span>{fee.cost}</span>
          </div>
        ))}
      {discount && (
        <div className="cart__sidemenu-footer-row color-error">
          <span>{translate('checkout.summary.discount')}</span>
          <span>{discount}</span>
        </div>
      )}
      <div className="cart__sidemenu-footer-row">
        <span>{translate('checkout.summary.vat')}</span>
        <span>{vat}</span>
      </div>
      <div className="cart__sidemenu-footer-row">
        <div>
          <strong>{translate('checkout.cart.total')}</strong>
          <span className="cart__sidemenu-footer-subtext">
            {translate('checkout.summary.including-vat')}
          </span>
        </div>
        <strong>{grandTotal}</strong>
      </div>
      <div className="cart__sidemenu-footer-row">
        <a href={checkoutUrl} className="btn btn--primary">
          {translate('minicart.checkout')}
        </a>
      </div>
    </>
  );

  const OrderRowItem = (row) => {
    const [internalQuantity, setInternalQuantity] = useState(row.quantity);

    const onQuantityChange = (value) => {
      updateOrderRowQuantity(row.articleNumber, value, row.rowSystemId);
    };

    return (
      <li key={row.rowSystemId} className="cart__sidemenu-orderrow">
        <div className="cart__sidemenu-orderrow-wrapper">
          <div className="cart__sidemenu-orderrow-images">
            {row.brandLogoUrl && !row.hideBrandInfoInListings ? (
              <img src={row.brandLogoUrl} loading="lazy" className="brand" />
            ) : null}
            <a href={row.url}>
              <img src={row.image} loading="lazy" />
            </a>
          </div>
          <div className="cart__sidemenu-orderrow-information-container">
            <div className="cart__sidemenu-orderrow-information">
              <div className="cart__sidemenu-orderrow-text">
                <span className="product__info--art-nr">
                  <span>{row.articleNumber}</span>
                  <CopyButton text={row.articleNumber} />
                </span>
                <h3 className="cart__sidemenu-orderrow-header">
                  <a className="color-grey-900" href={row.url}>
                    {!row.hideBrandInfoInListings && row.brandName}{' '}
                    {row.baseProductName}
                  </a>
                </h3>
                <span>{row.name}</span>
                {row.campaignMarking && (
                  <span className="cart__sidemenu-orderrow-campaign-tag">
                    {row.campaignMarking}
                  </span>
                )}
              </div>
              <div className="cart__sidemenu-orderrow-price">
                <span className="cart__sidemenu-orderrow-mainprice">
                  {row.totalCampaignPrice || row.totalPrice}
                </span>
                <span className="cart__sidemenu-orderrow-subprice">
                  {row.unitCampaignPrice || row.unitPrice}
                  {translate('minicart.st')}
                  {row.unitCampaignPrice && (
                    <span className="cart__sidemenu-orderrow-subprice-line">
                      {' '}
                      {row.unitPrice}
                      {translate('minicart.st')}
                    </span>
                  )}
                </span>
                {row.recyclingFee && (
                  <span className="cart__sidemenu-orderrow-subprice">
                    {translate('general.recycling-fee')} {row.recyclingFee}
                  </span>
                )}
              </div>
            </div>
            <div className="cart__sidemenu-orderrow-information-subcontainer show-for-large">
              <div className="cart__sidemenu-orderrow-information cart__sidemenu-orderrow-information--footer">
                <div className="cart__sidemenu-orderrow-information--stockstatus">
                  <ProductInStockTag {...row} />
                </div>
                <div className="cart__sidemenu-orderrow-quantity">
                  <QuantityInput
                    disabled={!row.canEdit || row.isFreeGift}
                    value={row.quantity}
                    id={row.rowSystemId}
                    onQuantityIncrease={() =>
                      onQuantityChange(parseInt(row.quantity) + 1)
                    }
                    onQuantityDecrease={() =>
                      onQuantityChange(parseInt(row.quantity) - 1)
                    }
                    onChange={(quantity) => onQuantityChange(quantity)}
                  />
                </div>
                {row.canEdit && (
                  <div className="cart__sidemenu-orderrow-remove">
                    <Trash
                      color={colors.error}
                      onClick={() => onQuantityChange(0)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="cart__sidemenu-orderrow-information-subcontainer hide-for-large ">
          <div className="cart__sidemenu-orderrow-information cart__sidemenu-orderrow-information--footer">
            <div className="cart__sidemenu-orderrow-information--stockstatus">
              <ProductInStockTag {...row} />
            </div>
            <div className="cart__sidemenu-orderrow-quantity">
              <QuantityInput
                disabled={!row.canEdit || row.isFreeGift}
                value={row.quantity}
                id={row.rowSystemId}
                onQuantityIncrease={() =>
                  onQuantityChange(parseInt(row.quantity) + 1)
                }
                onQuantityDecrease={() =>
                  onQuantityChange(parseInt(row.quantity) - 1)
                }
                onChange={(quantity) => onQuantityChange(quantity)}
              />
            </div>
            {row.canEdit && (
              <div className="cart__sidemenu-orderrow-remove">
                <Trash
                  color={colors.error}
                  onClick={() => onQuantityChange(0)}
                />
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  const orderRowItems = hasOrderRows
    ? Object.keys(orderRows).map((rowGroupKey) => (
        <div
          className="cart__sidemenu-orderrow-group"
          data-group={rowGroupKey}
          key={rowGroupKey}
        >
          {rowGroupKey !== 'no-group' && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <h6>{translate('order-row-group.title')}</h6>
              <CloseIcon
                cursor="pointer"
                onClick={() => dispatch(removeOrderRowGroup(rowGroupKey))}
              />
            </div>
          )}
          {orderRows[rowGroupKey].map((row) => (
            <OrderRowItem
              key={row.rowSystemId}
              {...row}
              canEdit={rowGroupKey === 'no-group'}
            />
          ))}
        </div>
      ))
    : null;

  return (
    <div
      className={`cart cart--mini ${addToCartAnimating ? 'cart--animate' : ''}`}
    >
      <a className="cart__button" onClick={() => onToggle()}>
        <div className="cart__icon">
          <span className="cart__quantity">{quantity}</span>
          <ShoppingCart color="#12377A" size={32} />
        </div>
        <span className="cart__title show-for-xlarge">
          <div>{translate('minicart.cart')}</div>
          {showMiniCartGrandTotal && <div>{miniCartGrandTotal}</div>}
        </span>
      </a>
      <SideMenu
        id="cart"
        className="side-menu--cart"
        open={showInfo}
        onClosed={() => onClose()}
        footer={hasOrderRows ? footer : null}
      >
        <div className="cart__sidemenu">
          <div className="row cart cart--mini cart__sidemenu-header">
            <div className="cart__button">
              <div className="cart__icon">
                <span className="cart__quantity">{quantity}</span>
                <ShoppingCart color="#12377A" size={32} />
              </div>
            </div>
            <span className="cart__title">
              {translate('minicart.cart')}
              <span>{miniCartGrandTotal}</span>
            </span>
          </div>
          <div className="cart__sidemenu-content">
            {hasOrderRows ? (
              <>
                <ul className="cart__sidemenu-orderrows">
                  {hasOrderRows && <>{orderRowItems}</>}
                </ul>
              </>
            ) : (
              <div className="cart__sidemenu-empty">
                <h4>{translate('checkout.cart.empty')}</h4>
              </div>
            )}
          </div>
        </div>
      </SideMenu>
    </div>
  );
};

export default (props) => renderForServer(MiniCart, props);
